import { useState } from 'react';
import { api } from '../lib/api';
import { readable } from '../pages/billing-pay';
import { useApi } from './kit';

type OwnerView = { owners: string[]; agency: string | null };

/** "Ask <owner>" in words, from the members who can pay. */
export function ownerLine(v: OwnerView | undefined): string {
  if (!v?.owners.length) return 'the workspace owner';
  return v.agency ? `${v.agency} (${v.owners.join(', ')})` : v.owners.join(', ');
}

/**
 * What a member who cannot pay sees instead of a Top up button: who can pay, and one click that emails them what is
 * needed with a link to Billing (once a day per member). `need` picks the link: credits → #buy-credits, plan → #plan
 * (or ?plan=builder|growth for a named plan).
 */
export function NotifyOwner({ need, plan, what, compact = false }: { need: 'credits' | 'plan'; plan?: 'builder' | 'growth'; what?: string; compact?: boolean }) {
  const q = useApi<OwnerView>('/portal/billing/owner');
  const [state, setState] = useState<{ kind: 'idle' | 'busy' | 'done' | 'already' | 'error'; text?: string }>({ kind: 'idle' });
  const who = ownerLine(q.data);
  const send = async () => {
    setState({ kind: 'busy' });
    try {
      const r = await api<{ already: boolean }>('/portal/billing/notify-owner', { method: 'POST', body: { need, ...(plan ? { plan } : {}) } });
      setState({ kind: r.already ? 'already' : 'done' });
    } catch (e) { setState({ kind: 'error', text: readable(e as Error & { fix?: string }) }); }
  };
  if (state.kind === 'already') return <span role="status">{who} already has your message from the last 24 hours.</span>;
  if (state.kind === 'done') return <span role="status">Told {who}: they have an email with a link to Billing.</span>;
  return (
    <span className="notify-owner">
      {what ?? 'Only the workspace owner can pay.'} Ask {who}.{' '}
      <button type="button" className={compact ? 'btn btn-sm' : 'btn'} disabled={state.kind === 'busy' || !q.data?.owners.length} onClick={send}>{state.kind === 'busy' ? 'Sending…' : 'Notify owner'}</button>
      {state.kind === 'error' ? <span className="error" role="alert"> {state.text}</span> : null}
    </span>
  );
}
